import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ContentWrapper } from '../ContentWrapper';
import { DisclaimerContainer } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { CNSOHDisclaimersContainer } from './CNSOHDisclaimerContainer';
import {
  CNSDisclaimersLinkStyles,
  HealthGradesStyled,
} from './DisclaimersContainer.styled';

interface Props {
  isGuestUser?: boolean;
  isOHCNS: boolean;
  search?: string;
  sectionType?: string;
}

type Disclaimer = {
  key: '';
  content: '';
};

export const CNSDisclaimersContainer = ({
  isGuestUser = false,
  isOHCNS,
  search,
  sectionType,
}: Props) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedDisclaimer, setSelectedDisclaimer] = useState<Disclaimer>();

  const lagoonData = useLagoon('ui-messaging')();

  const uhcDualCompletePlanDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.UHC_DUAL_COMPLETE_PLAN_DISCLAIMER,
  });

  const uhcConnectedDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.UHC_CONNECTED_DISCLAIMER,
  });

  const findCareDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.FIND_CARE_DISCLAIMER,
  });

  const guestGeneralDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.GUEST_GENERAL_DISCLAIMER,
  });

  const [enableMapViewHideOption] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const trackEvents = (isOpen: boolean, title: any): void => {
    if (!isOpen) {
      adobeLinkTrackEvent({ name: title, location: 'footer' });
      adobeModalTrackEvent(title);
    } else {
      adobeLinkTrackEvent({ name: 'close', location: `modal:${title}` });
    }
  };

  return (
    <HealthGradesStyled
      data-auto-testid="standard-disclaimer-content"
      data-testid="standard-disclaimer-content-for-cns"
    >
      <ContentWrapper>
        <DisclaimerContainer
          cssProps={{ ratingSection: true, enableMapViewHideOption }}
        >
          <Flex css={CNSDisclaimersLinkStyles} direction="column">
            <Link
              data-testid="uhc-dual-complete-plan-disclaimer"
              isStandardAnchor
              onClick={() => {
                setIsOpen(true);
                setSelectedDisclaimer({
                  key: uhcDualCompletePlanDisclaimer?.title,
                  content: uhcDualCompletePlanDisclaimer?.message,
                });
                trackEvents(isOpen, uhcDualCompletePlanDisclaimer?.title);
              }}
            >
              {uhcDualCompletePlanDisclaimer?.title}
            </Link>
            <Link
              data-testid="uhc-connected-disclaimer"
              isStandardAnchor
              onClick={() => {
                setIsOpen(true);
                setSelectedDisclaimer({
                  key: uhcConnectedDisclaimer?.title,
                  content: uhcConnectedDisclaimer?.message,
                });
                trackEvents(isOpen, uhcConnectedDisclaimer?.title);
              }}
            >
              {uhcConnectedDisclaimer?.title}
            </Link>
            <Link
              data-testid="find-care-disclaimer"
              isStandardAnchor
              onClick={() => {
                setIsOpen(true);
                setSelectedDisclaimer({
                  key: findCareDisclaimer?.title,
                  content: isGuestUser
                    ? guestGeneralDisclaimer?.message
                    : findCareDisclaimer?.message,
                });
                trackEvents(isOpen, findCareDisclaimer?.title);
              }}
            >
              {findCareDisclaimer?.title}
            </Link>
          </Flex>
          {isOHCNS ? (
            <CNSOHDisclaimersContainer
              search={search ?? ''}
              sectionType={sectionType}
            />
          ) : null}
        </DisclaimerContainer>
      </ContentWrapper>
      <Modal
        isDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position="right"
        title={selectedDisclaimer?.key}
      >
        <Modal.Section>
          <Markdown data-testid="cns-generic-disclaimer-modal-content">
            {selectedDisclaimer?.content}
          </Markdown>
        </Modal.Section>
        <Modal.Section>
          <Layout.Group alignLayout="right">
            <Button
              data-testid="disclaimer-modal-close"
              onClick={() => {
                setIsOpen(false);
                trackEvents(isOpen, selectedDisclaimer?.key);
              }}
            >
              {t(
                'ANTHEM_BLUE_CROSS_DISCLAIMER.ANTHEM_BLUE_CROSS_DISCLAIMER_POPUP.CLOSE_BUTTON'
              )}
            </Button>
          </Layout.Group>
        </Modal.Section>
      </Modal>
    </HealthGradesStyled>
  );
};
